import React from 'react'
import { cn } from '../../lib/utils'

interface BadgeProps {
  children: React.ReactNode
  variant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  className,
}) => {
  const variantClasses = {
    default: 'badge-default',
    secondary: 'badge-secondary',
    destructive: 'badge-destructive',
    outline: 'badge-outline',
    success: 'bg-green-100 text-green-800 border-green-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  }

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-xs px-2.5 py-0.5',
    lg: 'text-sm px-3 py-1',
  }

  return (
    <span
      className={cn(
        'badge',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
    >
      {children}
    </span>
  )
}

// Status badge component
interface StatusBadgeProps {
  status: string
  className?: string
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  className,
}) => {
  const getStatusVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
      case 'completed':
      case 'approved':
      case 'meets':
        return 'success'
      case 'pending':
      case 'in_progress':
      case 'analyzing':
      case 'partially_meets':
        return 'warning'
      case 'failed':
      case 'rejected':
      case 'not_met':
        return 'destructive'
      case 'draft':
      case 'not_evaluated':
        return 'secondary'
      default:
        return 'default'
    }
  }

  const formatStatus = (status: string) => {
    return status
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
  }

  return (
    <Badge
      variant={getStatusVariant(status)}
      className={className}
    >
      {formatStatus(status)}
    </Badge>
  )
}

// Priority badge component
interface PriorityBadgeProps {
  priority: string
  className?: string
}

export const PriorityBadge: React.FC<PriorityBadgeProps> = ({
  priority,
  className,
}) => {
  const getPriorityVariant = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
      case 'urgent':
        return 'destructive'
      case 'medium':
        return 'warning'
      case 'low':
        return 'success'
      default:
        return 'default'
    }
  }

  const formatPriority = (priority: string) => {
    return priority.charAt(0).toUpperCase() + priority.slice(1)
  }

  return (
    <Badge
      variant={getPriorityVariant(priority)}
      className={className}
    >
      {formatPriority(priority)}
    </Badge>
  )
}

// Count badge component
interface CountBadgeProps {
  count: number
  max?: number
  className?: string
}

export const CountBadge: React.FC<CountBadgeProps> = ({
  count,
  max = 99,
  className,
}) => {
  const displayCount = count > max ? `${max}+` : count.toString()
  
  return (
    <Badge
      variant="destructive"
      size="sm"
      className={cn('ml-1', className)}
    >
      {displayCount}
    </Badge>
  )
}

export default Badge